import React from 'react';

import {isCoreObject} from 'app-core/framework/CoreObjectIntrospectionUtils';
import CoreObjectStarIconRenderer, {
  StarredItemListingExtras,
} from 'app-core/platform/starrable/CoreObjectStarIcon';
import ArchiveRecordLabel from 'archive/components/ArchiveRecordLabel';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import BaseListingRendererWithPlugins from 'nav/expanded/BaseListingRendererWithPlugins';
import {
  ColumnAccessorKey,
  ColumnConfiguration,
  ListingRendererType,
} from 'nav/expanded/ListingRendererConstants';
import {SortKey} from 'search/SearchConstants';
import {getColumnHeaderFromSortKey} from 'search/SortUtil';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {DatetimeTableCell} from 'ui-library/scrollable/table-cells/DatetimeTableCell';
import {BasicEvent} from 'util/Types';

import {JellyfishListingGraphQL} from '../../../../graphql-fragments/app-core/archetype/__generated/JellyfishListingGraphQL';

export interface JellyfishListingExtras extends StarredItemListingExtras {}

export default class JellyfishListingRenderer extends BaseListingRendererWithPlugins<ListingRendererType.JELLYFISH> {
  listingRendererType = ListingRendererType.JELLYFISH;

  renderIcon(): JSX.Element {
    return <BenchlingIcon icon={ModelTypeConstants.JELLYFISH} className="scrollableTable-cell-icon" />;
  }

  getNonPluginColumnConfigurations(
    items: Array<JellyfishListingGraphQL>,
    {starredItemsByObjectId}: JellyfishListingExtras
  ): Array<ColumnConfiguration<JellyfishListingGraphQL>> {
    const columns: Array<ColumnConfiguration<JellyfishListingGraphQL>> = [
      {
        column: {
          accessorKey: ColumnAccessorKey.ID,
          header: getColumnHeaderFromSortKey(SortKey.SORT_IDENTIFIER_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_IDENTIFIER_KEY,
          width: 200,
        },
        render: (item) => (
          <span key="name" className="sidepanel-primaryLabel">
            {item.name}
            <span className="u-mini-spacing-left">
              <ArchiveRecordLabel item={item} />
            </span>
          </span>
        ),
      },
      {
        column: {
          header: getColumnHeaderFromSortKey(SortKey.SORT_STARRED),
          accessorKey: ColumnAccessorKey.STAR,
          isCollapsible: false,
          isSortable: true,
          sortKey: SortKey.SORT_STARRED,
          width: 70,
        },
        render: (item) =>
          starredItemsByObjectId &&
          isCoreObject(item) && (
            <CoreObjectStarIconRenderer item={item} isStarred={starredItemsByObjectId[item.id]} />
          ),
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.CREATED_AT,
          header: getColumnHeaderFromSortKey(SortKey.SORT_CREATED_AT_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_CREATED_AT_KEY,
        },
        render: (item) => <DatetimeTableCell datetime={item.createdAt} showTimeInCell={false} />,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.MODIFIED_AT,
          header: getColumnHeaderFromSortKey(SortKey.SORT_MODIFIED_AT_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_MODIFIED_AT_KEY,
        },
        render: (item) => <DatetimeTableCell datetime={item.modifiedAt} showTimeInCell={false} />,
      },
    ];

    return columns;
  }

  getNonPluginRowContentHeight(item: JellyfishListingGraphQL): number {
    // TODO(BNCH-52085): calculate height
    return 40;
  }

  openContextMenu(item: JellyfishListingGraphQL, e: BasicEvent, extras: unknown): void {
    // TODO(BNCH-52085): implement context menu
  }
}
